import React, { useEffect, useState } from "react";
import { api } from "../api";
import AOS from "aos";
import "aos/dist/aos.css";

import { Medal, Star, Trophy } from "lucide-react";

import Navbar2 from "../components/navbar/Navbar2";

const tabs = ["Daily Players", "Weekly Players", "30 Days Players"];

export default function LeaderBoard() {
  const [activeTab, setActiveTab] = useState(0);

  // ✅ DYNAMIC DATA
  const [leaderboard, setLeaderboard] = useState({
    daily: [],
    weekly: [],
    monthly: [],
  });

  const [loading, setLoading] = useState(true);

  // ===============================
  // FETCH API
  // ===============================
  const fetchLeaderBoard = async () => {
    try {
      setLoading(true);

      const { data } = await api.get("/api/leaderboard-report");
      // console.log("data : ", data);
      setLeaderboard({
        daily: data.daily || [],
        weekly: data.weekly || [],
        monthly: data.monthly || [],
      });
    } catch (err) {
      console.error("Leaderboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderBoard();

    AOS.init({
      offset: 100,
      duration: 500,
      easing: "ease-in-sine",
      delay: 100,
    });

    AOS.refresh();
  }, []);

  // ===============================
  // ACTIVE PLAYERS
  // ===============================
  const players =
    activeTab === 0
      ? leaderboard.daily
      : activeTab === 1
        ? leaderboard.weekly
        : leaderboard.monthly;

  // ===============================
  // MEDAL COLORS
  // ===============================
  const getMedalColor = (rank) => {
    if (rank === 1) return "text-yellow-400";
    if (rank === 2) return "text-slate-300";

    return "text-orange-400";
  };

  // ===============================
  // AVATAR LETTER
  // ===============================
  const getAvatarLetter = (name = "") => {
    return name?.charAt(0)?.toUpperCase();
  };

  return (
    <>
      {/* NAVBAR */}
      <div className="fixed left-0 right-0 top-0 z-50 bg-gradient-to-l from-cyan-950 via-cyan-900 to-cyan-950">
        <Navbar2 />
      </div>

      {/* PAGE */}
      <section className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e3354] to-[#020617] px-3 pb-10 pt-24 text-white sm:px-5">
        <div
          className="mx-auto w-full max-w-4xl rounded-[28px] border border-cyan-500/20 bg-[#1a2942]/90 p-3 shadow-[0_0_35px_rgba(0,255,255,0.08)] backdrop-blur-xl sm:p-5"
          data-aos="fade-up"
        >
          {/* HEADER */}
          <div className="pb-4">
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <Trophy className="text-yellow-400" size={28} />

              <h1 className="text-center text-2xl font-black tracking-wide text-yellow-400 sm:text-4xl">
                አሸናፊዎች & ተወዳዳሪዎች
              </h1>
            </div>

            <p className="mt-3 text-center text-sm text-cyan-100 sm:text-base">
              የዕለቱ ፣ የሳምንቱ እና የ30 ቀን ከፍተኛ ተጫዋቾች
            </p>

            <div className="mt-5 h-[1px] w-full bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
          </div>

          {/* TABS */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`rounded-2xl px-2 py-3 text-center text-xs font-bold transition-all duration-300 sm:px-4 sm:py-5 sm:text-base ${
                  activeTab === index
                    ? "bg-cyan-500 text-white shadow-[0_0_25px_rgba(0,255,255,0.35)]"
                    : "bg-[#163b58] text-cyan-100 hover:bg-[#1b4a6d]"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* LOADING */}
          {loading && (
            <div className="py-20 text-center text-cyan-200">
              Loading leaderboard...
            </div>
          )}

          {/* EMPTY */}
          {!loading && players.length === 0 && (
            <div className="py-20 text-center text-cyan-200">
              No leaderboard data found
            </div>
          )}

          {/* TOP PLAYERS */}
          {!loading && players.length > 0 && (
            <>
              <div className="mt-5 space-y-4">
                {players.slice(0, 3).map((player, index) => (
                  <div
                    key={player.playerPhone}
                    data-aos="fade-up"
                    className="flex items-center justify-between rounded-[20px] border border-yellow-500/40 bg-gradient-to-r from-[#00343d] via-[#00424c] to-[#00515d] px-3 py-3 shadow-lg transition-all duration-300 hover:scale-[1.01]"
                  >
                    {/* LEFT SIDE */}
                    <div className="flex items-center gap-3">
                      {/* AVATAR */}
                      <div className="relative">
                        <div className="flex h-[56px] w-[56px] items-center justify-center overflow-hidden rounded-full border-[2.5px] border-yellow-400 bg-purple-400 text-xl font-black text-white">
                          {getAvatarLetter(player.winnerName)}
                        </div>
                      </div>

                      {/* INFO */}
                      <div>
                        <h2 className="text-[22px] font-extrabold leading-none text-yellow-200">
                          {player.winnerName.slice(0, 8)}
                        </h2>

                        <div className="mt-1 text-[14px] font-semibold text-yellow-400">
                          {player.totalWinAmount.toLocaleString()} ብር
                        </div>

                        <div className="mt-1 text-xs text-cyan-100">
                          {player.totalGames} Wins
                        </div>
                      </div>
                    </div>

                    {/* RIGHT SIDE */}
                    <div className="flex items-center gap-3">
                      <button className="rounded-xl border border-yellow-500/50 bg-[#124450] px-4 py-[6px] text-xs font-bold text-yellow-300 shadow-inner">
                        ሽልማት
                      </button>

                      {/* MEDAL */}
                      <div className="relative flex items-center justify-center">
                        <Medal
                          size={42}
                          className={`${getMedalColor(
                            index + 1,
                          )} drop-shadow-[0_0_8px_rgba(255,215,0,0.8)]`}
                        />

                        <span className="absolute text-sm font-black text-black">
                          {index + 1}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* OTHER PLAYERS */}
              <div className="mt-4 space-y-3">
                {players.slice(3).map((player, idx) => (
                  <div
                    key={player.playerPhone}
                    data-aos="fade-up"
                    className="flex items-center justify-between rounded-xl border border-cyan-400/20 bg-[#046b8d] px-3 py-3 transition-all duration-300 hover:bg-[#0a7ca3] sm:px-5 sm:py-4"
                  >
                    {/* LEFT */}
                    <div className="flex min-w-0 items-center gap-3 sm:gap-5">
                      {/* RANK */}
                      {/* <div className="w-6 text-2xl font-black text-white sm:w-8 sm:text-4xl">
                        {idx + 4}
                      </div> */}

                      {/* AVATAR */}
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-cyan-200 bg-cyan-700 text-lg font-black text-white sm:h-16 sm:w-16">
                        {getAvatarLetter(player.winnerName)}
                      </div>

                      {/* INFO */}
                      <div className="min-w-0">
                        <h3 className="truncate text-base font-bold text-white sm:text-2xl">
                          {player.winnerName.slice(0, 10)}
                        </h3>

                        <p className="text-sm text-cyan-100 sm:text-base">
                          {player.totalWinAmount.toLocaleString()} ብር
                        </p>

                        {/* <p className="text-xs text-cyan-200">
                          {player.playerPhone}
                        </p> */}
                      </div>
                    </div>

                    {/* SCORE */}
                    <div className="ml-2 flex items-center gap-1 text-cyan-100 sm:gap-2">
                      <Star size={18} />

                      <span className="text-lg font-bold sm:text-2xl">
                        {player.totalGames}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
